import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  IconButton,
  CircularProgress
} from '@mui/material';
import {
  AutoAwesome as AIIcon,
  Close as CloseIcon
} from '@mui/icons-material';

import i18n from '../../i18n';
import { colors } from '../../themes/typography';
import { API_BASE_URL, createAuthHeader } from '../../config/api.config';
import AnimatedTypography from '../shared/AnimatedTypography';

/**
 * POS AI Analysis Panel Component
 * Right sidebar for AI-powered POS analysis insights
 * Integrated with backend API endpoint
 */
const stripRepeatedComparisons = (text) => {
  if (typeof text !== 'string') return text;
  const cleaned = text
    .replace(/\s*\(-?\d+(?:\.\d+)?%\s+vs\s+mes\s+anterior(?:,\s*-?\d+(?:\.\d+)?%\s+vs\s+mes\s+anterior)*\)/gi, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
  return cleaned;
};

const POSAIAnalysisPanel = ({ 
  analysisMode = 'cluster',
  selectedCluster = '',
  selectedSector = '',
  selectedRoute = '',
  selectedMachine = '',
  storeClusterId = '',
  sectorId = '',
  routeId = '',
  machineId = '',
  selectedNameOverride = '',
  selectedIdOverride = '',
  customerId = 'sanven',
  referencePeriod = null,
  referenceMonth = null,
  onClose,
  t = (key) => key // Default translation function
}) => {
  const [analysisData, setAnalysisData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const cacheRef = useRef({});
  const didCancel = useRef(false);
  const resolvedReference = referencePeriod || referenceMonth || null;
  const referencePeriodLabel = useMemo(() => {
    if (!resolvedReference) return null;
    if (resolvedReference.periodType === 'quarter' && resolvedReference.quarter && resolvedReference.year) {
      const label = t(`quarter_names.${resolvedReference.quarter}`, {
        defaultValue: `Q${resolvedReference.quarter}`
      });
      return `${label} ${resolvedReference.year}`;
    }
    if (resolvedReference.month && resolvedReference.year) {
      const date = new Date(resolvedReference.year, resolvedReference.month - 1, 1);
      const fallback = new Intl.DateTimeFormat(undefined, { month: 'long' }).format(date);
      const monthName = t(`month_names.${resolvedReference.month}`, { defaultValue: fallback });
      return `${monthName} ${resolvedReference.year}`;
    }
    return null;
  }, [resolvedReference, t]);

  const isQuarterPeriod = resolvedReference?.periodType === 'quarter';
  const performanceHeading = isQuarterPeriod
    ? 'Rendimiento Trimestral'
    : 'Rendimiento Mensual';
  const periodDescriptorLower = isQuarterPeriod ? 'trimestral' : 'mensual';
  const periodDescriptorCapitalized = isQuarterPeriod ? 'Trimestral' : 'Mensual';

  // Get the current selection name and context
  const getAnalysisContext = () => {
    if (analysisMode === 'machine') {
      return {
        type: 'Máquina',
        name: selectedNameOverride || selectedMachine || 'Sin selección'
      };
    }
    if (analysisMode === 'route') {
      return {
        type: 'Ruta',
        name: selectedNameOverride || selectedRoute || 'Sin selección'
      };
    }
    if (analysisMode === 'sector') {
      return {
        type: 'Sector',
        name: selectedNameOverride || selectedSector || 'Sin selección'
      };
    }
    return {
      type: 'Ubicación',
      name: selectedNameOverride || selectedCluster || 'Sin selección'
    };
  };

  const context = getAnalysisContext();

  const referenceSignatureKey = useMemo(() => {
    const periodType =
      resolvedReference?.periodType || (resolvedReference?.quarter ? 'quarter' : 'month');
    const monthToken = resolvedReference?.quarter
      ? `Q${resolvedReference.quarter}`
      : resolvedReference?.month
        ? `M${resolvedReference.month}`
        : 'current';
    const yearToken = resolvedReference?.year ? String(resolvedReference.year) : 'current';
    return `${periodType}:${monthToken}:${yearToken}`;
  }, [resolvedReference]);

  const effectiveEntityId = useMemo(() => {
    if (selectedIdOverride) return selectedIdOverride;
    if (analysisMode === 'machine') return machineId;
    if (analysisMode === 'sector') return sectorId;
    if (analysisMode === 'route') return routeId;
    return storeClusterId;
  }, [analysisMode, machineId, routeId, sectorId, storeClusterId, selectedIdOverride]);

  const effectiveEntityName = useMemo(() => {
    if (selectedNameOverride) return selectedNameOverride;
    if (analysisMode === 'machine') return selectedMachine;
    if (analysisMode === 'sector') return selectedSector;
    if (analysisMode === 'route') return selectedRoute;
    return selectedCluster;
  }, [analysisMode, selectedCluster, selectedMachine, selectedNameOverride, selectedRoute, selectedSector]);

  // Fetch POS AI Analysis data
  useEffect(() => {
    const analysisId = effectiveEntityId;

    if (!analysisId) {
      setAnalysisData(null);
      setLoading(false);
      setError(null);
      return;
    }

    didCancel.current = false;

    const periodTypePart = resolvedReference?.periodType || (resolvedReference?.quarter ? 'quarter' : 'month');
    const monthPart = resolvedReference?.quarter
      ? `Q${resolvedReference.quarter}`
      : resolvedReference?.month
        ? `M${resolvedReference.month}`
        : 'current';
    const yearPart = resolvedReference?.year ? String(resolvedReference.year) : 'current';
    const clientPart = customerId ?? 'sanven';
    // El idioma entra en la clave: el texto del diagnóstico lo redacta el backend, así que una
    // entrada cacheada en otro idioma no vale para esta.
    const cacheKey = `${analysisMode}:${analysisId}:${clientPart}:${periodTypePart}:${monthPart}:${yearPart}:${i18n.language}`;
    const cachedEntry = cacheRef.current[cacheKey];

    if (cachedEntry?.status === 'ready') {
      setAnalysisData(cachedEntry.data);
      setLoading(false);
      setError(null);
      return () => {
        didCancel.current = true;
      };
    }

    if (cachedEntry?.status === 'loading') {
      setLoading(true);
      setError(null);
      return () => {
        didCancel.current = true;
      };
    }

    if (cachedEntry?.status === 'error') {
      setAnalysisData(null);
      setLoading(false);
      setError(cachedEntry.error);
      return () => {
        didCancel.current = true;
      };
    }

    setAnalysisData(null);
    setLoading(true);
    setError(null);

    const token = localStorage.getItem('access_token');
    if (!token) {
      const message = 'No access token available';
      cacheRef.current[cacheKey] = { status: 'error', error: message };
      setError(message);
      setLoading(false);
      return () => {
        didCancel.current = true;
      };
    }

    const encodedAnalysisId = encodeURIComponent(analysisId);
    const encodedCustomerId = encodeURIComponent(clientPart);
    const params = new URLSearchParams({ clientId: encodedCustomerId });
    if (resolvedReference?.periodType === 'quarter' && resolvedReference.quarter && resolvedReference.year) {
      params.append('periodType', 'quarter');
      params.append('quarter', String(resolvedReference.quarter));
      params.append('year', String(resolvedReference.year));
    } else if (resolvedReference?.month && resolvedReference?.year) {
      params.append('periodType', resolvedReference?.periodType === 'quarter' ? 'quarter' : 'month');
      params.append('month', String(resolvedReference.month));
      params.append('year', String(resolvedReference.year));
    }

    const endpoint = analysisMode === 'sector'
      ? `${API_BASE_URL}/sectors/${encodedAnalysisId}/pos-ai-analysis?${params.toString()}`
      : analysisMode === 'route'
        ? `${API_BASE_URL}/routes/${encodedAnalysisId}/pos-ai-analysis?${params.toString()}`
        : analysisMode === 'machine'
          ? `${API_BASE_URL}/machines/${encodedAnalysisId}/pos-ai-analysis?${params.toString()}`
          : `${API_BASE_URL}/store-clusters/${encodedAnalysisId}/pos-ai-analysis?${params.toString()}`;

    // `createAuthHeader` (no una cabecera a mano): sin `Accept-Language` el navegador manda la
    // suya — Chrome en inglés envía `en-US,en;q=0.9` — y el middleware del backend resuelve el
    // idioma con ella, así que el diagnóstico volvía en inglés con la interfaz en español.
    const fetchPromise = fetch(endpoint, {
      headers: createAuthHeader(token)
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        cacheRef.current[cacheKey] = { status: 'ready', data };
        if (!didCancel.current) {
          setAnalysisData(data);
          setLoading(false);
          setError(null);
        }
        return data;
      })
      .catch((err) => {
        cacheRef.current[cacheKey] = { status: 'error', error: err.message };
        if (!didCancel.current) {
          console.error('Error fetching POS AI analysis:', err);
          setAnalysisData(null);
          setError(err.message);
          setLoading(false);
        }
        throw err;
      });

    cacheRef.current[cacheKey] = { status: 'loading', promise: fetchPromise };

    return () => {
      didCancel.current = true;
    };
  }, [effectiveEntityId, customerId, analysisMode, referenceSignatureKey]);

  // Format currency
  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '€0';
    const jsLocale = { es: 'es-ES', en: 'en-US', it: 'it-IT' }[i18n.language] || 'es-ES';
    return `€${Number(value).toLocaleString(jsLocale, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  // Format percentage with proper sign
  const formatPercentage = (value, withSign = true) => {
    if (value === null || value === undefined) return '0%';
    const sign = withSign && value > 0 ? '+' : '';
    return `${sign}${Number(value).toFixed(1)}%`;
  };

  // Determine performance color
  const getPerformanceColor = (value) => {
    if (value === null || value === undefined) return '#6b7280';
    return value > 0 ? '#15803d' : '#dc2626';
  };


  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100%',
      backgroundColor: 'white'
    }}>
      {/* Header */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        p: 1.5,
        borderBottom: '1px solid rgba(107, 70, 193, 0.1)',
        background: 'linear-gradient(135deg, rgba(107, 70, 193, 0.05) 0%, rgba(124, 58, 237, 0.05) 100%)',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <AIIcon sx={{ fontSize: '16px', color: '#6B46C1' }} />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            <Typography sx={{
              fontSize: '14px',
              fontWeight: 600,
              color: '#37352f'
            }}>
              {analysisMode === 'sector'
                ? `Contexto - Diagnóstico ${periodDescriptorLower} sector`
                : analysisMode === 'route'
                  ? `Contexto - Diagnóstico ${periodDescriptorLower} ruta`
                  : `Contexto - Diagnóstico ${periodDescriptorCapitalized}`}
            </Typography>
            {referencePeriodLabel && (
              <Typography
                sx={{
                  fontSize: '12px',
                  color: '#6B46C1',
                  textTransform: 'capitalize'
                }}
              >
                {referencePeriodLabel}
              </Typography>
            )}
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon sx={{ fontSize: '18px' }} />
        </IconButton>
      </Box>

      {/* Content */}
      <Box sx={{ 
        flex: 1,
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        overflowY: 'auto'
      }}>
        {/* Análisis Ubicación Header */}
        <Box sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderRadius: '6px',
          p: 1.5,
          border: '1px solid rgba(0, 0, 0, 0.08)',
          mb: 1
        }}>
          <Typography sx={{
            fontSize: '14px',
            color: colors.text.primary || '#37352f',
            fontWeight: 600,
            mb: 0.5
          }}>
            {analysisMode === 'sector'
              ? 'Análisis de Sector'
              : analysisMode === 'route'
                ? 'Análisis de Ruta'
                : analysisMode === 'machine'
                  ? 'Análisis de Máquina'
                  : 'Análisis Ubicación'}
          </Typography>
          <Typography sx={{
            fontSize: '12px',
            color: colors.text.secondary || '#6b7280',
            fontWeight: 500,
            mb: 0.5
          }}>
            {analysisData?.storeName || analysisData?.routeName || analysisData?.sectorName || context.name}
          </Typography>
          {analysisData?.sectorName && analysisData?.storeName && (
            <Typography sx={{
              fontSize: '13px',
              color: colors.text.secondary || '#6b7280',
              fontWeight: 700
            }}>
              Sector: {analysisData.sectorName}
            </Typography>
          )}
        </Box>

        {/* Loading State */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={24} sx={{ color: '#6B46C1' }} />
          </Box>
        )}

        {/* Error State */}
        {error && (
          <Box sx={{
            backgroundColor: 'rgba(220, 38, 38, 0.1)',
            borderRadius: '6px',
            p: 1.5,
            border: '1px solid rgba(220, 38, 38, 0.2)',
            mb: 2
          }}>
            <Typography sx={{
              fontSize: '12px',
              color: '#dc2626',
              fontWeight: 500
            }}>
              Error al cargar datos: {error}
            </Typography>
          </Box>
        )}

        {/* Insufficient Data State */}
        {analysisData?.insufficientData && (
          <Box sx={{
            backgroundColor: 'rgba(251, 191, 36, 0.1)',
            borderRadius: '6px',
            p: 2,
            border: '1px solid rgba(251, 191, 36, 0.3)',
            textAlign: 'center',
            mb: 2
          }}>
            <Typography sx={{
              fontSize: '13px',
              color: '#92400e',
              fontWeight: 600,
              mb: 0.5
            }}>
              Datos insuficientes para análisis
            </Typography>
            <Typography sx={{
              fontSize: '12px',
              color: '#78350f',
              fontWeight: 400
            }}>
              {analysisData.message || "No hay suficientes datos de ventas para generar un análisis del período actual."}
            </Typography>
          </Box>
        )}

        {/* KPI Boxes */}
        {analysisData && !analysisData.insufficientData && (
          <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
            {/* Rendimiento del período seleccionado */}
            <Box sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              borderRadius: '6px',
              p: 1.5,
              border: '1px solid rgba(0, 0, 0, 0.08)',
              flex: 1
            }}>
              <Typography sx={{
                fontSize: '12px',
                color: colors.text.secondary || '#6b7280',
                fontWeight: 500,
                mb: 0.5
              }}>
                {performanceHeading}
              </Typography>
              <Typography sx={{
                fontSize: '20px',
                color: getPerformanceColor(analysisData.kpis?.monthlyPerformanceRevenue),
                fontWeight: 700,
                mb: 0.5
              }}>
                {formatPercentage(analysisData.kpis?.monthlyPerformanceRevenue)}
              </Typography>
              <Typography sx={{
                fontSize: '11px',
                color: colors.text.secondary || '#6b7280',
                lineHeight: 1.3
              }}>
                {analysisData.kpis?.monthlyPerformanceRevenue > 0
                  ? (analysisMode === 'cluster'
                      ? 'Por encima de la media de las ubicaciones'
                      : analysisMode === 'sector'
                        ? 'Por encima de la media de los sectores'
                        : analysisMode === 'route'
                          ? 'Por encima de la media de todas las rutas'
                          : 'Por encima de la media de las máquinas')
                  : (analysisMode === 'cluster'
                      ? 'Por debajo de la media de las ubicaciones'
                      : analysisMode === 'sector'
                        ? 'Por debajo de la media de los sectores'
                        : analysisMode === 'route'
                          ? 'Por debajo de la media de todas las rutas'
                          : 'Por debajo de la media de las máquinas')}
              </Typography>
            </Box>

            {/* Facturación del período seleccionado */}
            <Box sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              borderRadius: '6px',
              p: 1.5,
              border: '1px solid rgba(0, 0, 0, 0.08)',
              flex: 1
            }}>
              <Typography sx={{
                fontSize: '12px',
                color: colors.text.secondary || '#6b7280',
                fontWeight: 500,
                mb: 0.5
              }}>
                {`Facturación ${periodDescriptorCapitalized}`}
              </Typography>
              <Typography sx={{
                fontSize: '16px',
                color: colors.text.primary || '#37352f',
                fontWeight: 700,
                mb: 0.25
              }}>
                {formatCurrency(analysisData.kpis?.monthlyRevenue)}
              </Typography>
              <Typography sx={{
                fontSize: '11px',
                color: colors.text.secondary || '#6b7280',
                lineHeight: 1.3
              }}>
                Unidades Vendidas: {Number(analysisData.kpis?.monthlyUnits || 0).toLocaleString({ es: 'es-ES', en: 'en-US', it: 'it-IT' }[i18n.language] || 'es-ES')}
              </Typography>
            </Box>
          </Box>
        )}

        {/* AI Recommendation Box */}
        {analysisData && !analysisData.insufficientData && (
          <Box sx={{
            background: 'linear-gradient(135deg, rgba(107, 70, 193, 0.05) 0%, rgba(124, 58, 237, 0.05) 100%)',
            borderRadius: '6px',
            border: '1px solid rgba(107, 70, 193, 0.2)',
            p: 2,
            backdropFilter: 'blur(10px)'
          }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <AIIcon sx={{ fontSize: '14px', color: '#6B46C1' }} />
              <Typography sx={{
                fontSize: '12px',
                color: '#37352f',
                fontWeight: 700,
                lineHeight: 1.3
              }}>
                {t('aiAgent') || 'URGE AI'}
              </Typography>
            </Box>

            {/* Subtitle */}
            <Typography sx={{
              fontSize: '13px',
              color: '#37352f',
              fontWeight: 600,
              mb: 2
            }}>
              {analysisMode === 'sector'
                ? `Diagnóstico ${periodDescriptorCapitalized} del Sector`
                : analysisMode === 'route'
                  ? `Diagnóstico ${periodDescriptorCapitalized} de la Ruta`
                  : analysisMode === 'machine'
                    ? `Diagnóstico ${periodDescriptorCapitalized} de la Máquina`
                    : `Diagnóstico ${periodDescriptorCapitalized} de la Ubicación`}
            </Typography>
            
            {/* AI Diagnostic Text as Bullet Points */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {Array.isArray(analysisData.diagnostics) && analysisData.diagnostics.length > 0 ? (
                analysisData.diagnostics.map((text, idx) => {
                  const diagnosticText = stripRepeatedComparisons(text);
                  if (!diagnosticText) return null;
                  return (
                    <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <Box
                        sx={{
                          width: '6px',
                          height: '6px',
                          borderRadius: '50%',
                          backgroundColor: '#6B46C1',
                          mt: '6px',
                          flexShrink: 0
                        }}
                      />
                      <AnimatedTypography
                        typewriterSpeed={5}
                        startDelay={idx * 800}
                        showCursor={false}
                        preserveStrong
                        sx={{
                          fontSize: '12px',
                          color: '#37352f',
                          fontWeight: 400,
                          lineHeight: 1.4
                        }}
                      >
                        {diagnosticText}
                      </AnimatedTypography>
                    </Box>
                  );
                })
              ) : (
                <Typography sx={{ fontSize: '12px', color: colors.text.secondary || '#6b7280' }}>
                  {t('posAi_noCurrentWeekData')}
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {/* No Data State */}
        {!loading && !error && !analysisData && !!effectiveEntityId && (
          <Box sx={{
            backgroundColor: 'rgba(107, 114, 128, 0.1)',
            borderRadius: '6px',
            p: 2,
            border: '1px solid rgba(107, 114, 128, 0.2)',
            textAlign: 'center'
          }}>
            <Typography sx={{
              fontSize: '12px',
              color: colors.text.secondary || '#6b7280',
              fontWeight: 500
            }}>
              {'No hay datos disponibles para la selección actual.'}
            </Typography>
          </Box>
        )}

        {/* No Selection State */}
        {!effectiveEntityId && (
          <Box sx={{
            backgroundColor: 'rgba(107, 114, 128, 0.1)',
            borderRadius: '6px',
            p: 2,
            border: '1px solid rgba(107, 114, 128, 0.2)',
            textAlign: 'center'
          }}>
            <Typography sx={{
              fontSize: '12px',
              color: colors.text.secondary || '#6b7280',
              fontWeight: 500
            }}>
              {'Selecciona una opción para ver el análisis IA.'}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default POSAIAnalysisPanel;
